import React, { memo, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import UpcomingSingleContent from '../dashboard/UpcomingSingleContent'
import { deleteById, getContentForReview } from '../../server/server'
import BigImageModal from './BigImageModal'
import ConfirmationModal from './ConfirmationModal'
import { notify } from '../../utils/util'
import i18n from '../../i18n'
import { ANALYTICS_EVENTS } from '../../analytics/events'
import useTrackedSurface from '../../analytics/useTrackedSurface'
import ContentListModal from './ContentListModal/ContentListModal'
import usePagedContentList, { assertOk } from './ContentListModal/usePagedContentList'
import { getContentKey } from '../content/ToGo/ContentCard'

const SCROLL_ID = 'review-content-scroll'
const fetchPage = (offset) => getContentForReview(offset).then(assertOk)

const ReviewContentModal = memo(({ isOpen, onClose, count, onCountChanged }) => {
    const { t } = useTranslation()
    const list = usePagedContentList({ fetchPage, enabled: isOpen })
    const [imageModalOpen, setImageModalOpen] = useState(false)
    const [selectedImage, setSelectedImage] = useState('')
    const [deleteModalOpen, setDelModalOpen] = useState(false)
    const [selContent, setSelContent] = useState(null)
    useTrackedSurface(ANALYTICS_EVENTS.DASHBOARD_CONTENT_REVIEW_VIEWED, {
        active: isOpen,
        content: 'Content for review',
    })

    const openDeleteForSingle = useCallback((content) => {
        setSelContent(content)
        setDelModalOpen(true)
    }, [])
    const closeDelModal = useCallback(() => setDelModalOpen(false), [])

    const openBigImage = useCallback((url) => {
        setSelectedImage(url)
        setImageModalOpen(true)
    }, [])
    const closeImageModal = useCallback(() => {
        setImageModalOpen(false)
        setSelectedImage('')
    }, [])

    const { reload } = list
    const onReviewed = useCallback(() => {
        reload()
        onCountChanged?.()
    }, [reload, onCountChanged])

    const handleDelete = async () => {
        const resp = await deleteById({ collection: 'content', id: selContent?.id })
        closeDelModal()
        if (resp?.status === 200) {
            onReviewed()
            notify(t('contentDeleted'))
        } else {
            notify(t('yourDataIsNotUpdated'))
        }
    }

    const renderRow = (content, index) => (
        <UpcomingSingleContent
            key={getContentKey(content, index)}
            content={content}
            index={index}
            onDelete={openDeleteForSingle}
            onOpenImage={openBigImage}
            reviewContent
            onReviewed={onReviewed}
        />
    )

    return (
        <ContentListModal
            isOpen={isOpen}
            onClose={onClose}
            onAfterClose={list.reset}
            title={t('Review content')}
            items={list.items}
            loading={list.loading}
            refreshing={list.refreshing}
            hasMore={list.hasMore}
            version={list.version}
            loadMore={list.loadMore}
            emptyText={t("You don't have any content to review")}
            renderRow={renderRow}
            scrollId={SCROLL_ID}
            closeButton={{ id: 'review-content-modal-close', label: t('Review content'), count }}
        >
            {isOpen && (
                <>
                    <ConfirmationModal
                        modalIsOpen={deleteModalOpen}
                        closeModal={closeDelModal}
                        confirmation={handleDelete}
                        message={t('doYouWantToDeleteThis')}
                        subMessage={i18n.language === 'en' ? `Delete ${selContent?.type} content?` : ''}
                    />
                    <BigImageModal modalIsOpen={imageModalOpen} closeModal={closeImageModal} selectedImage={selectedImage} />
                </>
            )}
        </ContentListModal>
    )
})

export default ReviewContentModal
